
import { logger } from "@/lib/logger";
import { WebsiteAnalysisResult, AnalysisErrorType } from "./types";
import * as Sentry from "@sentry/react";

/**
 * Parse the AI response into a structured result
 * @param aiResponse The raw response from AI
 * @returns Parsed analysis result
 */
export function parseAIResponse(aiResponse: string): WebsiteAnalysisResult {
  const span = Sentry.startTransaction({
    name: "parse-ai-response",
    op: "data.parse"
  });

  try {
    // First try direct JSON parsing
    return JSON.parse(aiResponse.trim()) as WebsiteAnalysisResult;
  } catch (parseError) {
    try {
      // If direct parsing fails, try to extract a JSON block
      const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]) as WebsiteAnalysisResult;
      }
      
      // Record the parsing attempt in Sentry
      Sentry.addBreadcrumb({
        category: 'parsing',
        message: 'Direct JSON parsing failed, attempting fallback to extract JSON block',
        level: 'warning',
        data: {
          responsePreview: aiResponse.substring(0, 100) + '...'
        }
      });
      
      // Last resort: create a minimal object with just the description
      logger.warn({
        message: "Failed to parse AI response as JSON, using fallback",
        context: { 
          responsePreview: aiResponse.substring(0, 200),
          fallbackUsed: true 
        }
      });
      
      span.finish();
      return {
        description: aiResponse,
        keywords: [],
        colors: []
      };
    } catch (fallbackError) {
      const errorMessage = fallbackError instanceof Error ? fallbackError.message : String(fallbackError);
      
      logger.error({
        message: "Failed to parse AI analysis response",
        error: fallbackError instanceof Error ? fallbackError : new Error(errorMessage),
        context: { 
          aiResponsePreview: aiResponse.substring(0, 200),
          errorType: AnalysisErrorType.PARSE_ERROR 
        }
      });

      Sentry.captureException(fallbackError, {
        tags: {
          error_type: AnalysisErrorType.PARSE_ERROR,
          operation: "parse_ai_response"
        },
        contexts: {
          ai_response: {
            preview: aiResponse.substring(0, 500),
            length: aiResponse.length
          }
        }
      });
      
      span.finish();
      throw new Error("Failed to parse analysis results");
    }
  }
}
